// Suppliers (Admin) — god-mode supplier records + retailer connections.
//
// Source-of-truth: /var/www/kitchenos/admin-standalone.html
//   page section:           line 1335 (#page-suppliers)
//   summary tiles:          line 3320-3324
//   grid render:            line 3326-3343
//   detail panel:           line 3346-3376
//   delete supplier:        line 3392-3398
//   Add Supplier modal:     line 1571-1593
//
// Backend wiring
// --------------
//   GET    /api/admin/suppliers          server.js:943 — list (real)
//   DELETE /api/admin/suppliers/:id      server.js:1035 — delete (real)
//   (POST / PUT / connections endpoints are NOT yet implemented; we POST
//   optimistically + toast if the server replies 404/501.)
//
// Admin-side vs retailer-side
// ---------------------------
// The retailer's Suppliers page shows the 4 trade suppliers they buy from
// (TKC/PWS/Omega/LDL) and lets them tweak their NEGOTIATED discount %.
// THIS page is different: it's Steve's god-mode list of every supplier on
// the platform, including pending sign-ups and suspended accounts. Steve
// can add new suppliers, edit records, view connected retailers, and
// approve/reject supplier-retailer connection requests.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../types";
import { setCurrentSupplier } from "../catalogue/state";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type SupplierStatus = "active" | "pending" | "suspended";

interface AdminSupplier {
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  brandColour: string;
  status: SupplierStatus;
  connectedRetailers: number;
  avgDiscountPct: number;
  catalogueSize: number;
  createdAt: string;
}

interface ConnectedRetailer {
  id: string;
  name: string;
  discountPct: number;
  status: "approved" | "pending";
  since: string;
}

interface RecentPromotion {
  id: string;
  title: string;
  discountPct: number;
  endsAt: string;
}

interface SupplierDetail {
  supplier: AdminSupplier;
  retailers: ConnectedRetailer[];
  promotions: RecentPromotion[];
}

/** Raw server shape from GET /api/admin/suppliers. */
interface ServerSupplier {
  id: string;
  name?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  productCategories?: string[];
  createdAt?: string;
  active?: boolean;
  slug?: string;
  brandColour?: string;
  status?: SupplierStatus;
  connectedRetailers?: number;
  avgDiscountPct?: number;
  catalogueSize?: number;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const PCT = new Intl.NumberFormat("en-GB", {
  style: "percent",
  maximumFractionDigits: 1,
});

const NUM = new Intl.NumberFormat("en-GB");

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "2-digit",
});

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40);
}

// ── Demo fallback ────────────────────────────────────────────────────────────
// Stable seed mirroring what Steve currently pitches (TKC / PWS / Omega / LDL)
// plus a couple of pending sign-ups so the admin UI demonstrates all states.

const DEMO_SUPPLIERS: AdminSupplier[] = [
  {
    id: "supp_tkc",
    name: "TKC Components",
    slug: "tkc",
    contactEmail: "trade@tkc.co.uk",
    brandColour: "#F59E0B",
    status: "active",
    connectedRetailers: 42,
    avgDiscountPct: 0.31,
    catalogueSize: 1280,
    createdAt: "2024-08-12T09:00:00Z",
  },
  {
    id: "supp_pws",
    name: "PWS (Premier Woodworking Supplies)",
    slug: "pws",
    contactEmail: "sales@pws.co.uk",
    brandColour: "#6CA0FF",
    status: "active",
    connectedRetailers: 38,
    avgDiscountPct: 0.27,
    catalogueSize: 2410,
    createdAt: "2024-09-04T09:00:00Z",
  },
  {
    id: "supp_omega",
    name: "Omega Plc",
    slug: "omega",
    contactEmail: "trade@omega-plc.co.uk",
    brandColour: "#6DE0B3",
    status: "active",
    connectedRetailers: 24,
    avgDiscountPct: 0.34,
    catalogueSize: 1560,
    createdAt: "2024-11-20T09:00:00Z",
  },
  {
    id: "supp_ldl",
    name: "LDL Kitchens",
    slug: "ldl",
    contactEmail: "trade@ldl-kitchens.co.uk",
    brandColour: "#C5A8FF",
    status: "active",
    connectedRetailers: 18,
    avgDiscountPct: 0.29,
    catalogueSize: 980,
    createdAt: "2025-02-08T09:00:00Z",
  },
  {
    id: "supp_blum",
    name: "Blum UK",
    slug: "blum",
    contactEmail: "info@blum.co.uk",
    brandColour: "#FF8EA0",
    status: "pending",
    connectedRetailers: 0,
    avgDiscountPct: 0,
    catalogueSize: 0,
    createdAt: "2026-04-22T09:00:00Z",
  },
  {
    id: "supp_hafele",
    name: "Häfele",
    slug: "hafele",
    contactEmail: "trade@hafele.co.uk",
    brandColour: "#9BD1FF",
    status: "suspended",
    connectedRetailers: 5,
    avgDiscountPct: 0.18,
    catalogueSize: 420,
    createdAt: "2025-06-01T09:00:00Z",
  },
];

function demoDetail(supplier: AdminSupplier): SupplierDetail {
  const retailers: ConnectedRetailer[] = [
    { id: "r_turner",   name: "Turner Kitchens",      discountPct: 0.33, status: "approved" as const, since: "2025-01-12T00:00:00Z" },
    { id: "r_oldham",   name: "Oldham Kitchens",      discountPct: 0.30, status: "approved" as const, since: "2025-03-04T00:00:00Z" },
    { id: "r_manc",     name: "Manchester Cucina",    discountPct: 0.28, status: "approved" as const, since: "2025-05-18T00:00:00Z" },
    { id: "r_leeds",    name: "Leeds Kitchen Studio", discountPct: 0.32, status: "pending"  as const, since: "2026-04-29T00:00:00Z" },
  ].slice(0, Math.max(2, Math.min(supplier.connectedRetailers, 4)));

  const promos: RecentPromotion[] = supplier.status === "active"
    ? [
        { id: "promo_1", title: "Spring carcass clearance", discountPct: 0.40, endsAt: "2026-06-15T00:00:00Z" },
        { id: "promo_2", title: "New range launch — Vivo",  discountPct: 0.25, endsAt: "2026-07-31T00:00:00Z" },
      ]
    : [];

  return { supplier, retailers, promotions: promos };
}

// ── Backend fetch ────────────────────────────────────────────────────────────

function normaliseSupplier(s: ServerSupplier): AdminSupplier {
  const name = s.name || s.id || "Unnamed supplier";
  return {
    id: s.id,
    name,
    slug: s.slug || slugify(name),
    contactEmail: s.email || "",
    brandColour: s.brandColour || "#F59E0B",
    status: s.status ?? (s.active === false ? "suspended" : "active"),
    connectedRetailers: s.connectedRetailers ?? 0,
    avgDiscountPct: s.avgDiscountPct ?? 0,
    catalogueSize: s.catalogueSize ?? (s.productCategories?.length ?? 0),
    createdAt: s.createdAt || "",
  };
}

async function fetchSuppliers(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ list: AdminSupplier[]; isDemo: boolean }> {
  try {
    const data = await ctx.api<ServerSupplier[] | { suppliers: ServerSupplier[] }>(
      "/admin/suppliers",
      { signal },
    );
    const arr = Array.isArray(data) ? data : (data?.suppliers ?? []);
    if (arr.length === 0) {
      return { list: DEMO_SUPPLIERS, isDemo: true };
    }
    return { list: arr.map(normaliseSupplier), isDemo: false };
  } catch (err) {
    if (signal.aborted) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return { list: DEMO_SUPPLIERS, isDemo: true };
    }
    throw err;
  }
}

async function fetchSupplierDetail(
  ctx: PageContext,
  supplier: AdminSupplier,
  signal: AbortSignal,
): Promise<SupplierDetail> {
  try {
    const data = await ctx.api<SupplierDetail>(
      `/admin/suppliers/${encodeURIComponent(supplier.id)}/detail`,
      { signal },
    );
    return data;
  } catch (err) {
    if (signal.aborted) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return demoDetail(supplier);
    }
    throw err;
  }
}

// ── Shell renderer ───────────────────────────────────────────────────────────

interface ShellRefs {
  summary: HTMLElement;
  banner: HTMLElement;
  tableBody: HTMLElement;
  drillWrap: HTMLElement;
  searchInput: HTMLInputElement;
  statusFilter: HTMLSelectElement;
  addBtn: HTMLButtonElement;
  refreshBtn: HTMLButtonElement;
}

function renderShell(container: HTMLElement): ShellRefs {
  container.innerHTML = `
    <section class="asup-page" aria-labelledby="asup-title">
      <header class="asup-head">
        <div>
          <h1 id="asup-title" class="asup-title">Suppliers (Admin)</h1>
          <p class="asup-sub">All supplier records on the platform — manage records, view connections, approve sign-ups.</p>
        </div>
        <div class="asup-head-actions">
          <button class="adm-btn-ghost adm-btn" data-role="refresh" type="button">Refresh</button>
          <button class="adm-btn" data-role="add" type="button">+ Add Supplier</button>
        </div>
      </header>

      <div class="asup-banner" data-role="banner" hidden></div>

      <div class="asup-summary adm-card" data-role="summary"></div>

      <div class="asup-toolbar">
        <input
          class="adm-input asup-search"
          data-role="search"
          type="search"
          placeholder="Search by name, slug or email…"
          autocomplete="off"
        />
        <select class="adm-select asup-filter" data-role="filter" aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div class="adm-card asup-table-wrap">
        <table class="adm-table asup-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Slug</th>
              <th>Status</th>
              <th class="asup-num">Retailers</th>
              <th class="asup-num">Avg disc.</th>
              <th class="asup-num">Catalogue</th>
              <th>Created</th>
              <th class="asup-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody data-role="tbody">
            <tr><td colspan="8" class="adm-loading">Loading suppliers…</td></tr>
          </tbody>
        </table>
      </div>

      <aside class="asup-drill" data-role="drill" hidden></aside>
    </section>
  `;

  return {
    summary: container.querySelector<HTMLElement>("[data-role='summary']")!,
    banner: container.querySelector<HTMLElement>("[data-role='banner']")!,
    tableBody: container.querySelector<HTMLElement>("[data-role='tbody']")!,
    drillWrap: container.querySelector<HTMLElement>("[data-role='drill']")!,
    searchInput: container.querySelector<HTMLInputElement>("[data-role='search']")!,
    statusFilter: container.querySelector<HTMLSelectElement>("[data-role='filter']")!,
    addBtn: container.querySelector<HTMLButtonElement>("[data-role='add']")!,
    refreshBtn: container.querySelector<HTMLButtonElement>("[data-role='refresh']")!,
  };
}

function renderSummary(host: HTMLElement, list: AdminSupplier[]): void {
  const total = list.length;
  const active = list.filter((s) => s.status === "active").length;
  const pending = list.filter((s) => s.status === "pending").length;
  const suspended = list.filter((s) => s.status === "suspended").length;
  const totalRetailers = list.reduce((s, x) => s + x.connectedRetailers, 0);
  const totalCatalogue = list.reduce((s, x) => s + x.catalogueSize, 0);

  host.innerHTML = `
    <div class="asup-stat"><div class="asup-stat-lbl">Total suppliers</div><div class="asup-stat-val">${total}</div></div>
    <div class="asup-stat"><div class="asup-stat-lbl">Active</div><div class="asup-stat-val asup-ok">${active}</div></div>
    <div class="asup-stat"><div class="asup-stat-lbl">Pending</div><div class="asup-stat-val asup-warn">${pending}</div></div>
    <div class="asup-stat"><div class="asup-stat-lbl">Suspended</div><div class="asup-stat-val asup-danger">${suspended}</div></div>
    <div class="asup-stat"><div class="asup-stat-lbl">Retailer links</div><div class="asup-stat-val">${NUM.format(totalRetailers)}</div></div>
    <div class="asup-stat"><div class="asup-stat-lbl">Catalogue rows</div><div class="asup-stat-val">${NUM.format(totalCatalogue)}</div></div>
  `;
}

function renderTable(host: HTMLElement, list: AdminSupplier[]): void {
  if (list.length === 0) {
    host.innerHTML = `<tr><td colspan="8" class="adm-empty">No suppliers match your filters.</td></tr>`;
    return;
  }
  host.innerHTML = list
    .map((s) => {
      const statusClass =
        s.status === "active"
          ? "adm-pill-ok"
          : s.status === "pending"
          ? "adm-pill-warn"
          : "adm-pill-danger";
      const statusLabel = s.status[0]!.toUpperCase() + s.status.slice(1);
      return `
        <tr data-id="${escapeHtml(s.id)}" tabindex="0" role="button" aria-label="Drill into ${escapeHtml(s.name)}">
          <td>
            <span class="asup-swatch" style="background:${escapeHtml(s.brandColour)}"></span>
            <span class="asup-name">${escapeHtml(s.name)}</span>
          </td>
          <td><code class="asup-slug">${escapeHtml(s.slug)}</code></td>
          <td><span class="adm-pill ${statusClass}">${statusLabel}</span></td>
          <td class="asup-num">${NUM.format(s.connectedRetailers)}</td>
          <td class="asup-num">${s.avgDiscountPct > 0 ? PCT.format(s.avgDiscountPct) : "—"}</td>
          <td class="asup-num">${NUM.format(s.catalogueSize)}</td>
          <td>${escapeHtml(formatDate(s.createdAt))}</td>
          <td class="asup-actions-col">
            <button class="adm-btn-ghost adm-btn asup-mini" data-action="edit"      data-id="${escapeHtml(s.id)}" type="button">Edit</button>
            <button class="adm-btn-ghost adm-btn asup-mini" data-action="catalogue" data-id="${escapeHtml(s.id)}" type="button">Catalogue</button>
            <button class="adm-btn-ghost adm-btn asup-mini asup-mini-danger" data-action="suspend" data-id="${escapeHtml(s.id)}" type="button">${s.status === "suspended" ? "Reactivate" : "Suspend"}</button>
          </td>
        </tr>
      `;
    })
    .join("");
}

function renderDrill(host: HTMLElement, detail: SupplierDetail): void {
  const s = detail.supplier;
  host.hidden = false;
  host.innerHTML = `
    <header class="asup-drill-head">
      <div class="asup-drill-title">
        <span class="asup-swatch asup-swatch--lg" style="background:${escapeHtml(s.brandColour)}"></span>
        <div>
          <h2>${escapeHtml(s.name)}</h2>
          <p class="asup-drill-sub">${escapeHtml(s.contactEmail || "no contact email")} · slug <code>${escapeHtml(s.slug)}</code> · created ${escapeHtml(formatDate(s.createdAt))}</p>
        </div>
      </div>
      <button class="adm-btn-ghost adm-btn" data-role="drill-close" type="button" aria-label="Close drill-down">Close ×</button>
    </header>

    <div class="asup-drill-grid">
      <section class="adm-card">
        <h3 class="asup-drill-h3">Record</h3>
        <dl class="asup-dl">
          <dt>Status</dt><dd><span class="adm-pill ${s.status === "active" ? "adm-pill-ok" : s.status === "pending" ? "adm-pill-warn" : "adm-pill-danger"}">${escapeHtml(s.status)}</span></dd>
          <dt>Catalogue size</dt><dd>${NUM.format(s.catalogueSize)} rows</dd>
          <dt>Connected retailers</dt><dd>${NUM.format(s.connectedRetailers)}</dd>
          <dt>Avg negotiated discount</dt><dd>${s.avgDiscountPct > 0 ? PCT.format(s.avgDiscountPct) : "—"}</dd>
        </dl>
      </section>

      <section class="adm-card">
        <h3 class="asup-drill-h3">Connected retailers (${detail.retailers.length})</h3>
        ${
          detail.retailers.length === 0
            ? `<p class="adm-empty">No retailers connected yet.</p>`
            : `<ul class="asup-conn-list">${detail.retailers
                .map(
                  (r) => `
                  <li class="asup-conn-row">
                    <div class="asup-conn-main">
                      <span class="asup-conn-name">${escapeHtml(r.name)}</span>
                      <span class="asup-conn-meta">since ${escapeHtml(formatDate(r.since))} · ${PCT.format(r.discountPct)} disc.</span>
                    </div>
                    <div class="asup-conn-action">
                      ${
                        r.status === "pending"
                          ? `<span class="adm-pill adm-pill-warn">Pending</span>
                             <button class="adm-btn-ghost adm-btn asup-mini" data-action="conn-approve" data-supplier="${escapeHtml(s.id)}" data-retailer="${escapeHtml(r.id)}" type="button">Approve</button>
                             <button class="adm-btn-ghost adm-btn asup-mini asup-mini-danger" data-action="conn-reject" data-supplier="${escapeHtml(s.id)}" data-retailer="${escapeHtml(r.id)}" type="button">Reject</button>`
                          : `<span class="adm-pill adm-pill-ok">Approved</span>`
                      }
                    </div>
                  </li>
                `,
                )
                .join("")}</ul>`
        }
      </section>

      <section class="adm-card">
        <h3 class="asup-drill-h3">Recent promotions</h3>
        ${
          detail.promotions.length === 0
            ? `<p class="adm-empty">No active promotions.</p>`
            : `<ul class="asup-promo-list">${detail.promotions
                .map(
                  (p) => `<li><strong>${escapeHtml(p.title)}</strong> — ${PCT.format(p.discountPct)} off, ends ${escapeHtml(formatDate(p.endsAt))}</li>`,
                )
                .join("")}</ul>`
        }
      </section>
    </div>
  `;
}

// ── Add Supplier modal ───────────────────────────────────────────────────────

interface AddSupplierResult {
  name: string;
  slug: string;
  contactEmail: string;
  brandColour: string;
}

/**
 * The server found an existing supplier with this name under a different slug.
 * Recoverable, unlike every other create failure: the operator can either fix
 * the name or say "yes, really" and re-submit.
 */
class DuplicateNameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DuplicateNameError";
  }
}

/**
 * ApiError carries the raw response text as its message (see @tkos/api-client),
 * so the server's machine-readable `code` has to be read back out of it. Only
 * DUPLICATE_NAME is confirmable — SLUG_TAKEN is a hard stop, since the slug is
 * the supplier's identity key.
 */
function duplicateNameMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.message) as { code?: string; error?: string };
    if (body.code !== "DUPLICATE_NAME") return null;
    return body.error ?? "A supplier with that name already exists.";
  } catch {
    return null;   // not JSON — treat as an ordinary failure
  }
}

function openAddSupplierModal(
  onSubmit: (data: AddSupplierResult, confirmDuplicateName: boolean) => Promise<void>,
): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "asup-dialog";
  dialog.innerHTML = `
    <form method="dialog" class="asup-form">
      <h2 class="asup-form-title">Add Supplier</h2>
      <label class="asup-field">
        <span>Name</span>
        <input class="adm-input" name="name" required maxlength="120" autocomplete="off" />
      </label>
      <label class="asup-field">
        <span>Slug</span>
        <input class="adm-input" name="slug" required maxlength="40" pattern="[a-z0-9-]+" placeholder="auto-generated from name" />
      </label>
      <label class="asup-field">
        <span>Contact email</span>
        <input class="adm-input" name="contactEmail" type="email" required maxlength="160" autocomplete="off" />
      </label>
      <label class="asup-field">
        <span>Brand colour</span>
        <input class="adm-input asup-colour" name="brandColour" type="color" value="#F59E0B" />
      </label>
      <div class="asup-form-err" data-role="form-err" hidden></div>
      <footer class="asup-form-actions">
        <button class="adm-btn-ghost adm-btn" value="cancel" type="submit">Cancel</button>
        <button class="adm-btn" value="confirm" type="submit" data-role="confirm">Add Supplier</button>
      </footer>
    </form>
  `;
  document.body.appendChild(dialog);

  const form = dialog.querySelector<HTMLFormElement>(".asup-form")!;
  const nameInput = form.elements.namedItem("name") as HTMLInputElement;
  const slugInput = form.elements.namedItem("slug") as HTMLInputElement;
  const err = dialog.querySelector<HTMLElement>("[data-role='form-err']")!;

  // Set once the operator has been warned about a same-name supplier and
  // chosen to proceed.
  let confirmDuplicateName = false;

  let touchedSlug = false;
  slugInput.addEventListener("input", () => {
    touchedSlug = true;
  });
  nameInput.addEventListener("input", () => {
    if (!touchedSlug) slugInput.value = slugify(nameInput.value);
    // Editing the name after a warning means it no longer applies — otherwise
    // a second, different duplicate would sail straight through unwarned.
    if (confirmDuplicateName) {
      confirmDuplicateName = false;
      const btn = dialog.querySelector<HTMLButtonElement>("[data-role='confirm']");
      if (btn) btn.textContent = "Add Supplier";
      err.hidden = true;
    }
  });

  form.addEventListener("submit", (ev) => {
    const submitter = (ev as SubmitEvent).submitter as HTMLButtonElement | null;
    if (submitter?.value !== "confirm") return; // cancel
    ev.preventDefault();
    err.hidden = true;
    const data: AddSupplierResult = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value.trim(),
      slug: (form.elements.namedItem("slug") as HTMLInputElement).value.trim() || slugify(nameInput.value),
      contactEmail: (form.elements.namedItem("contactEmail") as HTMLInputElement).value.trim(),
      brandColour: (form.elements.namedItem("brandColour") as HTMLInputElement).value,
    };
    if (!data.name || !data.contactEmail || !data.slug) {
      err.textContent = "Name, slug and contact email are required.";
      err.hidden = false;
      return;
    }
    const confirm = dialog.querySelector<HTMLButtonElement>("[data-role='confirm']")!;
    confirm.disabled = true;
    confirm.textContent = "Saving…";
    void onSubmit(data, confirmDuplicateName)
      .then(() => {
        dialog.close("confirm");
      })
      .catch((e: unknown) => {
        confirm.disabled = false;
        if (e instanceof DuplicateNameError) {
          // Warn in place rather than stacking a second dialog: the form stays
          // open so the obvious fix — correct the name — is one edit away, and
          // pressing the button again goes ahead.
          confirmDuplicateName = true;
          confirm.textContent = "Create anyway";
          err.textContent = e.message;
          err.hidden = false;
          nameInput.focus();
          nameInput.select();
          return;
        }
        confirmDuplicateName = false;
        confirm.textContent = "Add Supplier";
        err.textContent = e instanceof Error ? e.message : "Couldn't save supplier.";
        err.hidden = false;
      });
  });

  dialog.addEventListener("close", () => {
    dialog.remove();
  });

  dialog.showModal();
  nameInput.focus();
  return dialog;
}

// ── Page ─────────────────────────────────────────────────────────────────────

interface State {
  all: AdminSupplier[];
  filtered: AdminSupplier[];
  selectedId: string | null;
  search: string;
  statusFilter: "all" | SupplierStatus;
  isDemo: boolean;
}

const suppliersAdminPage: Page = {
  name: "suppliers",
  label: "Suppliers (Admin)",
  icon: "🏭",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const refs = renderShell(container);

    const state: State = {
      all: [],
      filtered: [],
      selectedId: null,
      search: "",
      statusFilter: "all",
      isDemo: false,
    };

    let controller: AbortController | null = null;
    let detailController: AbortController | null = null;
    let disposed = false;
    let activeDialog: HTMLDialogElement | null = null;

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        refs.banner.hidden = true;
        refs.banner.textContent = "";
        refs.banner.classList.remove("asup-banner--error");
        return;
      }
      refs.banner.hidden = false;
      refs.banner.textContent = msg;
      refs.banner.classList.toggle("asup-banner--error", kind === "error");
    };

    const applyFilters = (): void => {
      const q = state.search.trim().toLowerCase();
      state.filtered = state.all.filter((s) => {
        if (state.statusFilter !== "all" && s.status !== state.statusFilter) return false;
        if (!q) return true;
        return (
          s.name.toLowerCase().includes(q) ||
          s.slug.toLowerCase().includes(q) ||
          s.contactEmail.toLowerCase().includes(q)
        );
      });
    };

    const renderEverything = (): void => {
      renderSummary(refs.summary, state.all);
      renderTable(refs.tableBody, state.filtered);
    };

    const closeDrill = (): void => {
      detailController?.abort();
      detailController = null;
      state.selectedId = null;
      refs.drillWrap.hidden = true;
      refs.drillWrap.replaceChildren();
    };

    const openDrill = async (id: string): Promise<void> => {
      const supplier = state.all.find((s) => s.id === id);
      if (!supplier) return;
      detailController?.abort();
      detailController = new AbortController();
      state.selectedId = id;
      refs.drillWrap.hidden = false;
      refs.drillWrap.innerHTML = `<div class="adm-loading">Loading supplier…</div>`;
      try {
        const detail = await fetchSupplierDetail(ctx, supplier, detailController.signal);
        if (disposed || detailController?.signal.aborted) return;
        renderDrill(refs.drillWrap, detail);
      } catch (err) {
        if (detailController?.signal.aborted) return;
        const msg = err instanceof Error ? err.message : "Couldn't load supplier";
        refs.drillWrap.innerHTML = `<div class="adm-error">${escapeHtml(msg)}</div>`;
      }
    };

    const load = async (): Promise<void> => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      refs.tableBody.innerHTML = `<tr><td colspan="8" class="adm-loading">Loading suppliers…</td></tr>`;
      try {
        const { list, isDemo } = await fetchSuppliers(ctx, signal);
        if (disposed || signal.aborted) return;
        state.all = list;
        state.isDemo = isDemo;
        applyFilters();
        renderEverything();
        if (isDemo) {
          setBanner("Live suppliers feed unavailable — showing demo seed.", "info");
        } else {
          setBanner(null);
        }
      } catch (err) {
        if (signal.aborted) return;
        const msg = err instanceof Error ? err.message : "Couldn't load suppliers";
        state.all = DEMO_SUPPLIERS;
        state.isDemo = true;
        applyFilters();
        renderEverything();
        setBanner(`${msg} — showing demo seed.`, "error");
        ctx.showToast(msg, "error");
      }
    };

    // ── Event handlers ─────────────────────────────────────────────────────

    const onSearchInput = (): void => {
      state.search = refs.searchInput.value;
      applyFilters();
      renderTable(refs.tableBody, state.filtered);
    };

    const onFilterChange = (): void => {
      const v = refs.statusFilter.value as State["statusFilter"];
      state.statusFilter = v;
      applyFilters();
      renderTable(refs.tableBody, state.filtered);
    };

    const onRefreshClick = (): void => {
      void load();
    };

    const onAddClick = (): void => {
      if (activeDialog) return;
      // The optimistic 404/501 fallback that used to live here is gone.
      // POST /api/admin/suppliers is implemented now, so that branch could only
      // ever fire on a genuine fault — and its behaviour (invent a local id,
      // toast "saved locally", lose it on reload) would hide the fault behind
      // what looks like a successful save. Real failures now surface.
      activeDialog = openAddSupplierModal(async (data, confirmDuplicateName) => {
        try {
          const created = await ctx.api<{ supplier: ServerSupplier }>(
            "/admin/suppliers",
            {
              method: "POST",
              body: JSON.stringify({ ...data, confirmDuplicateName }),
            },
          );
          const normalised = normaliseSupplier(created.supplier);
          state.all = [normalised, ...state.all];
          applyFilters();
          renderEverything();
          ctx.showToast(`Added ${normalised.name}`, "success");
        } catch (err) {
          const dupe = duplicateNameMessage(err);
          if (dupe) throw new DuplicateNameError(dupe);
          throw err;
        }
      });
      activeDialog.addEventListener("close", () => {
        activeDialog = null;
      });
    };

    const handleRowAction = async (action: string, id: string): Promise<void> => {
      const supplier = state.all.find((s) => s.id === id);
      if (!supplier) return;
      if (action === "edit") {
        ctx.showToast(`Edit ${supplier.name} — not yet wired`, "info");
        return;
      }
      if (action === "catalogue") {
        setCurrentSupplier({ id, name: supplier.name });
        ctx.navigate("catalogue");
        return;
      }
      if (action === "suspend") {
        const next: SupplierStatus = supplier.status === "suspended" ? "active" : "suspended";
        const verb = next === "suspended" ? "Suspend" : "Reactivate";
        if (!window.confirm(`${verb} ${supplier.name}?`)) return;
        try {
          await ctx.api(`/admin/suppliers/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify({ status: next }),
          });
        } catch (err) {
          if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 501)) {
            ctx.showToast("Couldn't update supplier", "error");
            return;
          }
          // 404/501 → optimistic.
        }
        supplier.status = next;
        applyFilters();
        renderEverything();
        ctx.showToast(`${verb}d ${supplier.name}`, "success");
        return;
      }
    };

    const handleConnAction = async (
      action: "conn-approve" | "conn-reject",
      supplierId: string,
      retailerId: string,
    ): Promise<void> => {
      try {
        await ctx.api(
          `/admin/suppliers/${encodeURIComponent(supplierId)}/connections/${encodeURIComponent(retailerId)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ status: action === "conn-approve" ? "approved" : "rejected" }),
          },
        );
        ctx.showToast(`Connection ${action === "conn-approve" ? "approved" : "rejected"}`, "success");
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          ctx.showToast(`Connection ${action === "conn-approve" ? "approved" : "rejected"} (saved locally)`, "info");
        } else {
          ctx.showToast("Couldn't update connection", "error");
          return;
        }
      }
      // Refresh drill.
      if (state.selectedId) {
        void openDrill(state.selectedId);
      }
    };

    const onTableClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const actionBtn = target.closest<HTMLButtonElement>("button[data-action]");
      if (actionBtn) {
        ev.stopPropagation();
        const action = actionBtn.dataset.action!;
        const id = actionBtn.dataset.id!;
        void handleRowAction(action, id);
        return;
      }
      const row = target.closest<HTMLTableRowElement>("tr[data-id]");
      if (row) {
        void openDrill(row.dataset.id!);
      }
    };

    const onTableKeydown = (ev: KeyboardEvent): void => {
      if (ev.key !== "Enter" && ev.key !== " ") return;
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const row = target.closest<HTMLTableRowElement>("tr[data-id]");
      if (!row) return;
      ev.preventDefault();
      void openDrill(row.dataset.id!);
    };

    const onDrillClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest("[data-role='drill-close']")) {
        closeDrill();
        return;
      }
      const connBtn = target.closest<HTMLButtonElement>("button[data-action^='conn-']");
      if (connBtn) {
        const action = connBtn.dataset.action as "conn-approve" | "conn-reject";
        const supplierId = connBtn.dataset.supplier!;
        const retailerId = connBtn.dataset.retailer!;
        void handleConnAction(action, supplierId, retailerId);
      }
    };

    refs.searchInput.addEventListener("input", onSearchInput);
    refs.statusFilter.addEventListener("change", onFilterChange);
    refs.refreshBtn.addEventListener("click", onRefreshClick);
    refs.addBtn.addEventListener("click", onAddClick);
    refs.tableBody.addEventListener("click", onTableClick);
    refs.tableBody.addEventListener("keydown", onTableKeydown);
    refs.drillWrap.addEventListener("click", onDrillClick);

    void load();

    return (): void => {
      disposed = true;
      controller?.abort();
      detailController?.abort();
      controller = null;
      detailController = null;
      refs.searchInput.removeEventListener("input", onSearchInput);
      refs.statusFilter.removeEventListener("change", onFilterChange);
      refs.refreshBtn.removeEventListener("click", onRefreshClick);
      refs.addBtn.removeEventListener("click", onAddClick);
      refs.tableBody.removeEventListener("click", onTableClick);
      refs.tableBody.removeEventListener("keydown", onTableKeydown);
      refs.drillWrap.removeEventListener("click", onDrillClick);
      if (activeDialog && activeDialog.open) {
        activeDialog.close();
      }
      activeDialog?.remove();
      activeDialog = null;
    };
  },
};

registerPage(suppliersAdminPage);
